import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import {
  ENABLE_TUITION,
  ENABLE_SUPERSET,
  ENABLE_POSITIONS,
  ENABLE_RESEARCH,
  ENABLE_AI,
  ENABLE_COURSES,
  ENABLE_CAPITAL,
  isFeatureEnabled,
} from '../utils/env';

const LicenseContext = createContext(null);

// Module definitions for UI display
const MODULE_INFO = {
  budget: {
    name: 'Budget Planning',
    description: 'Core budgeting, scenario planning, and performance tracking',
    icon: '💰',
    features: [
      'Budget Overview',
      'Department Planning',
      'Budget Scenarios',
      'Budget Model',
    ],
  },
  tuition: {
    name: 'Tuition & Fee Planning',
    description: 'Revenue modeling, fee structures, enrollment scenarios, COA management',
    icon: '📚',
    features: [
      'Cost of Attendance Editor',
      'Fee Components Manager',
      'Discount Policies',
      'Fee Projection Tool',
      'Enrollment Scenarios',
      'Retention & Cohort Analysis',
    ],
  },
  reporting: {
    name: 'Analytics BI',
    description: 'BI dashboards, custom reports, advanced analytics',
    icon: '📊',
    features: [
      'BI Integration',
      'Custom Dashboard Builder',
      'Advanced Analytics',
      'Scheduled Reports',
    ],
  },
  positions: {
    name: 'Position Management',
    description: 'PCNs, FTE splits, funding grids, compensation planning',
    icon: '👥',
    features: [
      'Position Budgeting',
      'PCN Management',
      'FTE Split Tracking',
      'Funding Grids',
      'Compensation Planning',
      'Personnel What-If Analysis',
    ],
  },
  research: {
    name: 'Research & Grants',
    description: 'NOA AI extraction, portfolio management, payroll mappings',
    icon: '🔬',
    features: [
      'Research Commitment Management',
      'NOA AI Extraction',
      'Portfolio Dashboard',
      'Payroll Mappings',
      'Research Transaction Linking',
      'Grant Closeout Management',
    ],
  },
  ai: {
    name: 'AI Features',
    description: 'Report generation, validation rules, smart suggestions',
    icon: '🤖',
    features: [
      'AI Report Generation',
      'Smart Validation Rules',
      'Intelligent Suggestions',
      'Document Analysis',
    ],
  },
  courses: {
    name: 'Course & Section Planning',
    description: 'Course scheduling, section management, capacity planning',
    icon: '📖',
    features: [
      'Course Scheduling',
      'Section Management',
      'Capacity Planning',
      'Enrollment Projections',
    ],
  },
  capital: {
    name: 'Capital Planning',
    description:
      'Multi-year capital projects, asset tracking, debt service schedules',
    icon: '🏗️',
    features: [
      'Capital Project Management',
      'Asset Tracking & Depreciation',
      'Funding Source Management',
      'Debt Service Schedules',
      'Cash Flow Projections',
      'Balance Sheet Impact Analysis',
    ],
  },
};

// Bundle tier definitions
const BUNDLE_INFO = {
  core: {
    name: 'Core',
    description: 'Budget planning, workflows, forms, base reporting',
  },
  better: {
    name: 'Better',
    description: 'Core + Tuition & Fee Planning + Reporting & Dashboards',
  },
  best: {
    name: 'Best',
    description: 'Better + Position Management',
  },
  premier: {
    name: 'Premier',
    description: 'All modules included',
  },
};

export const LicenseProvider = ({ children }) => {
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [featureFlags, setFeatureFlags] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch feature flags (no auth required)
  const fetchFeatureFlags = useCallback(async () => {
    try {
      const response = await api.get('/license/feature-flags');
      setFeatureFlags(response.data || {});
      return response.data;
    } catch (err) {
      console.warn('Failed to fetch feature flags:', err);
      // Default to env vars if API fails
      setFeatureFlags({
        ENABLE_TUITION,
        ENABLE_SUPERSET,
        ENABLE_POSITIONS,
        ENABLE_RESEARCH,
        ENABLE_AI,
        ENABLE_COURSES,
        ENABLE_CAPITAL,
      });
      return null;
    }
  }, []);

  // Fetch full license info (requires auth)
  const fetchLicenseInfo = useCallback(async () => {
    try {
      const response = await api.get('/license/info');
      setLicenseInfo(response.data);
      return response.data;
    } catch (err) {
      // Not an error if user isn't logged in
      if (err.response?.status !== 401) {
        console.warn('Failed to fetch license info:', err);
      }
      return null;
    }
  }, []);

  // Initial load - fetch feature flags
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchFeatureFlags();
      setLoading(false);
    };
    init();
  }, [fetchFeatureFlags]);

  // Check if a specific module is enabled
  const isModuleEnabled = useCallback((module) => {
    if (module === 'budget') return true;
    // Map module names to feature flag keys
    const flagMap = {
      tuition: 'ENABLE_TUITION',
      reporting: 'ENABLE_SUPERSET',
      positions: 'ENABLE_POSITIONS',
      research: 'ENABLE_RESEARCH',
      ai: 'ENABLE_AI',
      courses: 'ENABLE_COURSES',
      capital: 'ENABLE_CAPITAL',
    };

    const flagKey = flagMap[module];
    if (!flagKey) return false;

    // Check API response first, then fall back to env vars
    if (featureFlags[flagKey] !== undefined) {
      return featureFlags[flagKey];
    }

    // Fall back to environment variables
    return isFeatureEnabled(flagKey.replace('ENABLE_', ''));
  }, [featureFlags]);

  // Get list of all enabled modules
  const getEnabledModules = useCallback(() => {
    return Object.keys(MODULE_INFO).filter(isModuleEnabled);
  }, [isModuleEnabled]);

  // Get module info with enabled status
  const getModuleInfo = useCallback((module) => {
    return {
      ...MODULE_INFO[module],
      enabled: isModuleEnabled(module),
    };
  }, [isModuleEnabled]);

  // Get current bundle tier
  const getBundleTier = useCallback(() => {
    return licenseInfo?.bundle_tier || 'core';
  }, [licenseInfo]);

  // Get bundle info
  const getBundleInfo = useCallback(() => {
    const tier = getBundleTier();
    return BUNDLE_INFO[tier] || BUNDLE_INFO.core;
  }, [getBundleTier]);

  const value = {
    // State
    licenseInfo,
    featureFlags,
    loading,
    error,

    // Methods
    fetchFeatureFlags,
    fetchLicenseInfo,
    isModuleEnabled,
    getEnabledModules,
    getModuleInfo,
    getBundleTier,
    getBundleInfo,

    // Static data
    MODULE_INFO,
    BUNDLE_INFO,
  };

  return (
    <LicenseContext.Provider value={value}>
      {children}
    </LicenseContext.Provider>
  );
};

// Hook to use license context
export const useLicense = () => {
  const context = useContext(LicenseContext);
  if (!context) {
    throw new Error('useLicense must be used within a LicenseProvider');
  }
  return context;
};

// Convenience hooks for checking specific modules
export const useModuleEnabled = (module) => {
  const { isModuleEnabled } = useLicense();
  return isModuleEnabled(module);
};

export const useTuitionEnabled = () => useModuleEnabled('tuition');
export const useReportingEnabled = () => useModuleEnabled('reporting');
export const usePositionsEnabled = () => useModuleEnabled('positions');
export const useResearchEnabled = () => useModuleEnabled('research');
export const useAIEnabled = () => useModuleEnabled('ai');
export const useCoursesEnabled = () => useModuleEnabled('courses');

export default LicenseContext;
